import {BackEndService} from "src/utils/BackEndService";
import {analyticsController} from "../../user/analytics/AnalyticsController";

// @ts-ignore false negative due to intro.js packaging
import introJs from 'intro.js';

import {Hint, HintsGroup} from "./models/Hint";
import {Tour, TourStep} from "./models/Tour";
import {introJsData} from './models/introJsData';
import {IntroductionType} from "./models/IntroTypes";
import PlayerData from './models/data/PlayerData'

import { Screen } from 'quasar'

class IntroJsService {
  private hintsInstance: any;
  private toursInstance: any;

  /* Data */
  private introductions: any = {
    tours: [] as Tour[],
    hints: [] as HintsGroup[]
  };

  private progressQueue: any = [];
  private hisDoingRequests: boolean = false;

  private idByHints: Map<number, Hint[]> = new Map();
  private idByTours: Map<number, any> = new Map();

  private tourTimeout: any = -1;

  /* Setup */

  constructor() {
    const isMobileText = this.isMobile();

    this.toursInstance = introJs();
    this.toursInstance.setOptions({
      disableInteraction: true,
      exitOnEsc: false,
      exitOnOverlayClick: false,
      nextLabel: isMobileText ? '>' : 'Suivant',
      prevLabel: isMobileText ? '<' : 'Précédent',
      doneLabel: isMobileText ? 'x' : 'Terminer',
      hidePrev: true,
      nextToDone: true,
      scrollToElement: true,
      showBullets: false
    });

    this.hintsInstance = introJs();
    this.hintsInstance.setOptions({
      hintButtonLabel: "C'est compris",
    })
  }

  public init(introductionData: any) {
    this.addProgressToIntroductionData(introductionData.hints, 'hints');
    this.addProgressToIntroductionData(introductionData.tours, 'tours');
  }

  private addProgressToIntroductionData(list: any[], target: IntroductionType) {
    const config = introJsData[target];
    this.introductions[target] = list.map(elem => {
      const data = config.find((c: any) => c.name === elem.name) || {};
      if (!elem.progress) {
        elem.progress = {current_step: 0, done: false}
      }
      return {...elem, ...data};
    });
  }

  /* Interface */

  public displayHintsIfNeeded(names: any) {
    this.resetHints();

    names.map((name: string) => {
      if (this.shouldDisplayIntroduction(name, 'hints')) {
        const {id} = this.introductions.hints.find((intro: any) => intro.name === name);
        this.idByHints.set(id, this.getIntroductionSteps(name, 'hints'));
      }
    })

    if (this.idByHints.size > 0) {
      this.displayHints();
    }
  }

  public displayToursIfNeeded(names: any, isTutorial = false) {
    try {
      this.resetTour();
      const isMobile = this.isMobile();
      names.map((name: string) => {
        if (this.shouldDisplayIntroduction(name, 'tours')) {
          const {id, progress} = this.introductions.tours.find((intro: any) => intro.name === name);
          let tourSteps = this.getIntroductionSteps(name, 'tours');

          tourSteps = tourSteps.filter((step: TourStep) => {
            return (isMobile ? !step.isOnlyDesktop : !step.isOnlyMobile) && (isTutorial || !step.isForTutorialOnly);
          });

          const currentStep = progress ? progress.current_step : 0;
          this.idByTours.set(id, {name, tourSteps, currentStep});
        }
      })

      if (this.idByTours.size > 0) {
        this.tourTimeout = setTimeout(() => {
          this.displayTours();
        }, 500);
      }
    }
    catch (error) {
      console.error('Error displaying introjs tours: ' + error);
    }
  }

  public forceDisplayTour(names: string[]) {
    this.resetTour();

    const toursForced = PlayerData.tours.filter((tour: Tour) => names.includes(tour.name));
    this.tourTimeout = setTimeout(() => {
      this.displayForcedTours(toursForced);
    }, 500);
  }

  public shouldDisplayIntroduction(name: string, type: IntroductionType) {
    const introduction = this.getIntroduction(name, type);
    if (!introduction.progress || !introduction.progress.done) {
      const displayConditions = introduction.display_condition || [];
      return displayConditions.every((condition: any) => this.isConditionValid(condition));
    }
    return false;
  }

  private isConditionValid(condition: any) {
    if (condition.type === 'analytics') {
      if (!analyticsController.isConditionValid(condition)) {
        return false;
      }
    }
    else if (condition.type === 'tours') {
      const conditionIntroduction: any = this.getIntroduction(condition.name, 'tours');
      if (condition.amount === 0) {
        return !conditionIntroduction.done
      }
      return (conditionIntroduction.progress && conditionIntroduction.done)
    }
    else {
      console.error(`Condition Type ${condition.type} is not handled for introJs.`)
      return false;
    }
    return true;
  }

  private displayHints() {
    this.hintsInstance.setOptions({
      hints: [].concat.apply([], Array.from(this.idByHints.values()) as any)
    }).onhintclose((val: any) => {
      const correspondingIntroId: number = Array.from(this.idByHints.keys())[val];
      this.progressQueue.push({introduction_id: correspondingIntroId, completed: true})
      this.completeIntroduction(correspondingIntroId, 'hints');

      if (!this.hisDoingRequests) {
        this.updateProgress();
      }
    }).addHints();
  }

  private displayTours() {
    let canChangeStep = false;
    let prevStepId: number = -1;
    const toursIds: number[] = Array.from(this.idByTours.keys());
    const toursDetails: any[] = Array.from(this.idByTours.values());
    const steps: TourStep[] = [].concat.apply([], toursDetails.map((tour) => tour.tourSteps));

    this.toursInstance.setOptions({
      steps: steps,
      showProgress: steps.length > 1,
    })

    this.toursInstance.onbeforechange(() => {
      const currentStep: number = this.toursInstance._currentStep;
      const stepDetails: TourStep = steps[currentStep];
      if (stepDetails.beforeChange) {
        stepDetails.beforeChange(this.toursInstance._introItems, currentStep);
      }
      this.toursInstance.refresh();
    });

    this.toursInstance.onchange(() => {
      const currentStep: number = this.toursInstance._currentStep;
      const stepDetails: TourStep = steps[currentStep];
      const playingIntroId: number = toursIds[toursDetails.findIndex((tour) => tour.tourSteps.includes(stepDetails))];
      const currentTourData: any = this.idByTours.get(playingIntroId);
      const playingIntroLength: number = currentTourData.tourSteps.length;

      if (stepDetails.onStepEnter) {
        stepDetails.onStepEnter();
      }
      if (canChangeStep) {
        const predStep = steps[prevStepId];
        if (predStep && predStep.onStepLeave) {
          predStep.onStepLeave();
        }

        analyticsController.logAnalyticsEvent({title: `switched step in tour`, category: 'Tour Progress', additionalParameters: {step: currentStep % playingIntroLength, tour: currentTourData.name}})
        if (currentStep > prevStepId) {
          // this.progressQueue.push({introduction_id: playingIntroId, progress_value: currentStep % playingIntroLength});
          // Disable distant progress (only keep when completed) for now since it may lead to bugs
          const tourIndex = this.introductions.tours.findIndex((tour: any) => tour.id === playingIntroId);
          this.introductions.tours[tourIndex].progress.current_step = currentStep % playingIntroLength;

          // @ts-ignore
          if (this.toursInstance._currentStep === playingIntroLength) {
            this.idByTours.delete(playingIntroId);
            analyticsController.logAnalyticsEvent({title: `completed tour`, category: 'Tour Progress', additionalParameters: {tour: currentTourData.name}})
            this.progressQueue.push({introduction_id: playingIntroId, progress_value: currentStep % playingIntroLength, completed: true});
            this.completeIntroduction(playingIntroId, 'tours');
          }
          if (!this.hisDoingRequests) {
            this.updateProgress();
          }
        }
      }

      prevStepId = currentStep;
      canChangeStep = true;
    })

    this.toursInstance.oncomplete(() => {
      const currentStep: number = this.toursInstance._currentStep;
      const stepDetails: TourStep = steps[currentStep];
      // const toursIds: number[] = Array.from(this.idByTours.keys());
      const playingIntroId: number = toursIds[toursDetails.findIndex((tour) => tour.tourSteps.includes(stepDetails))];

      if (prevStepId >= steps.length - 1) {
        this.idByTours.delete(playingIntroId);
        BackEndService.sendPostRequest('introductions/progress', {introduction_id: playingIntroId, completed: true})
          .then(() => {
            this.completeIntroduction(playingIntroId, 'tours');
          })
      }
    })

    if (toursDetails[0].currentStep === 0) {
      this.toursInstance.start();
    }
    else {
      this.toursInstance.goToStepNumber(toursDetails[0].currentStep).start();
    }
  }

  private displayForcedTours(toursForced: Tour[]) {
    const toursDetails: any[] = toursForced.map(({ steps, ...rest }) => ({...rest, currentStep: 0, tourSteps: steps}));
    const steps: TourStep[] = [].concat.apply([], toursDetails.map((tour) => tour.tourSteps));

    this.toursInstance.setOptions({
      steps: steps,
      showProgress: steps.length > 1,
    })

    let canChangeStep = false;
    let prevStepId: number = -1;

    this.toursInstance.onbeforechange(() => {
      const currentStep: number = this.toursInstance._currentStep;
      const stepDetails: TourStep = steps[currentStep];
      if (stepDetails.beforeChange) {
        stepDetails.beforeChange(this.toursInstance._introItems, currentStep);
      }
      this.toursInstance.refresh();
    });

    this.toursInstance.onchange(() => {
      const currentStep: number = this.toursInstance._currentStep;
      const stepDetails: TourStep = steps[currentStep];

      if (stepDetails.onStepEnter) {
        stepDetails.onStepEnter();
      }
      if (canChangeStep) {
        const predStep = steps[prevStepId];
        if (predStep && predStep.onStepLeave) {
          predStep.onStepLeave();
        }
      }

      prevStepId = currentStep;
      canChangeStep = true;
    })

    this.toursInstance.start();
  }

  private updateProgress() {
    if (this.progressQueue.length > 0) {
      this.hisDoingRequests = true;
      BackEndService.sendPostRequest('introductions/progress', this.progressQueue[0])
        .then(() => {
          this.progressQueue.shift();
          this.updateProgress();
        })
    }
    else {
      this.hisDoingRequests = false;
    }
  }

  private resetHints() {
    this.idByHints = new Map();
  }

  private resetTour() {
    this.idByTours = new Map();
  }

  /* Helpers */

  private completeIntroduction(id: number, type: IntroductionType) {
    const introIndex = this.introductions[type].findIndex((tour: any) => tour.id === id);
    this.introductions[type][introIndex].progress.done = true;
  }

  public removeAllDisplayedHints() {
    document.querySelectorAll('.introjs-hint').forEach((hint: any) => {
      hint.parentElement.removeChild(hint);
    })
  }

  public stopTour() {
    clearTimeout(this.tourTimeout);
    this.toursInstance.exit(true);
    this.toursInstance._options.steps = [];
    this.toursInstance._introItems = [];
  }

  public getIntroduction(name: string, type: IntroductionType) {
    const introduction = this.introductions[type].find((intro: any) => intro.name === name);
    if (!introduction) {
      console.error(`Can't find introduction ${name} for ${type}`);
    }
    return introduction;
  }

  private getIntroductionSteps(name: string, type: IntroductionType): any {
    const introduction = this.introductions[type].find((intro: any) => intro.name === name);
    return introduction.steps || introduction.hints || [];
  }

  public isMobile() {
    return window.innerHeight < Screen.sizes.sm || window.innerWidth < Screen.sizes.md;
  }
}

export const introJsService = new IntroJsService();
